/**
 * NewsFeedPanel — GDELT/news articles with sentiment badges.
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

interface NewsArticle {
  id: string;
  title: string;
  source: string;
  url: string;
  published_at: string;
  sentiment: number | null;
  relevance_score: number | null;
  summary?: string;
}

function sentimentBadge(sentiment: number | null) {
  if (sentiment === null) return null;
  if (sentiment > 0.3)
    return <span className="rounded bg-emerald-950 px-1 text-[10px] text-emerald-400">Bullish</span>;
  if (sentiment < -0.3)
    return <span className="rounded bg-red-950 px-1 text-[10px] text-red-400">Bearish</span>;
  return <span className="rounded bg-zinc-800 px-1 text-[10px] text-zinc-400">Neutral</span>;
}

export function NewsFeedPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ["news", "latest"],
    queryFn: () =>
      apiGet<{ articles: NewsArticle[] }>("/api/news/latest?limit=50"),
    refetchInterval: 60_000,
  });

  const articles = data?.articles ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-zinc-500">
        Loading news...
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-zinc-950">
      <div className="flex-1 overflow-auto">
        {articles.length === 0 ? (
          <div className="mt-8 text-center text-xs text-zinc-600">
            No news articles
          </div>
        ) : (
          articles.map((a) => (
            <a
              key={a.id}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block border-b border-zinc-800/50 px-3 py-2 hover:bg-zinc-900/50"
            >
              <div className="mb-0.5 flex items-center gap-2">
                <span className="text-[10px] text-zinc-500">{a.source}</span>
                {sentimentBadge(a.sentiment)}
                <span className="ml-auto text-[10px] text-zinc-600">
                  {new Date(a.published_at).toLocaleTimeString()}
                </span>
              </div>
              <p className="text-xs leading-snug text-zinc-300 line-clamp-2">
                {a.title}
              </p>
            </a>
          ))
        )}
      </div>
    </div>
  );
}
